import { FC } from 'react';
import { toast } from 'sonner';
import { useConfirmPracticalHearingMutation } from '@teachur/http-client';
import { useSingleCourse } from '@new/features/courses/contexts/SingleCourseProvider';
import { useRequireSubscribed } from '@new/features/subscription/hooks/useRequireSubscribed';
import { Button } from '@new/common/ui';
import { Video } from '@new/common/ui/Video/Video';
import { getErrorMessage } from '@new/lib';
import { StepMessage } from '../StepMessage';

const PRACTICAL_COURSE_VIDEO_URL = 'https://www.youtube.com/watch?v=F6zU_ZLZmnM';

export const PracticalHearingContent: FC = () => {
  const { course, activeStep } = useSingleCourse();
  const requireSubscribed = useRequireSubscribed();

  const confirmPracticalHearingMutation = useConfirmPracticalHearingMutation({
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to confirm the video'));
    },
  });

  if (activeStep !== 'hearing' || !course) return null;

  const isLocked = !course.projectCompleted;
  const isWatched = course.coursePassed;

  const onConfirm = requireSubscribed(() => {
    if (isWatched || isLocked) return;
    confirmPracticalHearingMutation.mutate(course.id);
  });

  return (
    <div className="flex flex-col gap-6 rounded-2xl border-2 border-(--neutral-black-NB200) bg-[#FAFDFF] p-6 lg:p-8">
      <div className="flex flex-col gap-4">
        {isLocked && (
          <StepMessage
            variant="warning"
            message="You need to complete the Course Project first. Submit your project and wait for it to be approved before unlocking this step."
          />
        )}
        <div className="flex items-center justify-start gap-2">
          <h4 className="[font-size:var(--fs-h6)]! lg:[font-size:var(--fs-h4)]! text-(--neutral-black-NB1000)! font-bold m-0!">
            How the Course Hearing works
          </h4>
        </div>

        <div className="h-[1.5px] bg-(--neutral-black-NB200)" />

        <p className="[font-size:var(--fs-p4)]! lg:[font-size:var(--fs-p3)]! text-(--neutral-black-NB900) leading-[150%] m-0!">
          For your other courses, this final step is a live Course Hearing: a one-on-one meeting
          where an instructor reviews your work before passing you. Watch the short video below to
          learn what the Course Hearing is, how to schedule a meeting with an instructor, and what
          to expect during the session, so you are ready when you reach it in a real course.
        </p>

        <Video videoUrl={PRACTICAL_COURSE_VIDEO_URL} />

        {isWatched ? (
          <StepMessage
            variant="info"
            message="Great, you have finished this course. You now know how the Course Hearing works and how to schedule one."
          />
        ) : (
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={isLocked || confirmPracticalHearingMutation.isPending}
            className="self-center"
          >
            {confirmPracticalHearingMutation.isPending
              ? 'Confirming...'
              : 'I watched the video and understand how to schedule a Course Hearing'}
          </Button>
        )}
      </div>
    </div>
  );
};
